import useEditor, {type EditorProps, editorProps} from "../Editor";
import {computed, reactive} from "vue";
import type {FormEnvironment} from "../../Types";
import {setter, getter} from "../../Part";
import type {
    SgColumnImageType,
    SgColumnType, SgMap,
    SgMapDirect,
    SuggestionItem,
    SuggestionsInstructions
} from "@t/search/Suggester";
import type {ReverseSuggesterQuery, Suggester, TagProperty} from "@t/fd/editor/Suggester";
import type {Cast} from "@t/fd/editor/FormEditor";

export interface SuggesterProps extends EditorProps {
    canNew: boolean
}

export const suggesterProps = {
    ...editorProps,
    /* Can suggest new value */
    canNew: {
        type: Boolean,
        default: false,
    }
};


export interface Segment {
    key: string;
    type: string;
    index: number;
}

export interface TagSegment extends Segment{
    type: "tag";
    /** Label displayed on the tag */
    name: string;
    icon?: string;
    href?: string;
    /** Title diplayed on mouse over the tag */
    title?: string;
    id?: string | number;
}

export interface EditorSegment extends Segment {
    type: "editor",
    query: string
}



export interface ColumnData {
    content: string;
    type: "text" | "html" | "icon" | "img";
    width?: number;
    height?: number;
}
export interface NormalizedSuggestionItem {
    title: SgMapDirect;
    id: SgMapDirect;
    columns: ColumnData[];
    expand?: string;
}


function isStandardSuggestionItemProperty(prop:string):boolean {
    if (prop === "id" || prop === "title" || prop === "expand") return true;
    return (/c\d+/.test(prop));
}

/**
 * Process result item with has data in c1..cN columns to more suitable format
 */
export function processResultItem(ritem:SuggestionItem, instructions:SuggestionsInstructions):NormalizedSuggestionItem {
    const it = normalizeIdTitle(ritem);
    if (ritem.expand) it.expand = ritem.expand;
    it.columns = [];

    // Process columns
    let c:number = 1;
    let col;
    while (typeof (col = ritem[`c${c}`]) === "string") {
        const cobj:ColumnData = {
            content: col,
            type: "text"
        };
        setColumnType(cobj, c - 1, instructions);
        it.columns.push(cobj);
        c++;
    }
    // Process non-standard
    for (let prop of Object.keys(ritem)) {
        if (isStandardSuggestionItemProperty(prop)) continue;
        // @ts-ignore
        it[prop] = ritem[prop];
    }
    return it;
}

function isImage(colType: SgColumnType):colType is SgColumnImageType {
    return colType.startsWith("img ");
}

function setColumnType(cobj:ColumnData, idx:number, instructions:SuggestionsInstructions):void {
    if (instructions.columnTypes) {
        const type = instructions.columnTypes[idx];
        if (isImage(type)) {
            // Set image size
            cobj.type = "img";
            const arr = type.split(" ");
            cobj.width = parseInt(arr[1]);
            cobj.height = parseInt(arr[2]);
        } else cobj.type = type;
        return;
    }
    cobj.type = "text";
}

function normalizeIdTitle(ritem: SuggestionItem):NormalizedSuggestionItem {
    const normalized:NormalizedSuggestionItem = {
        id: normalizeOneIdTitle(ritem.id),
        title: normalizeOneIdTitle(ritem.title),
        columns: []
    };
    return normalized;
}

/**
 * This normalization is done because of compatibility with earlier implementations.
 * See suggester-format.md document
 */
function normalizeOneIdTitle(idOrTitle:SgMap):SgMapDirect {
    if (typeof idOrTitle === "string" || typeof idOrTitle === "number") {
        return { main: idOrTitle };
    }
    return idOrTitle;
}

function dataIsArray(data:any):data is Array<any> {
    return Array.isArray(data);
}

export const SELF_PATH = "*";

export default function(props: SuggesterProps, env:FormEnvironment) {

    const ed = useEditor(props);
    const sg = computed<Suggester>(() => props.part as Suggester);
	const meta = initMetaArray();


	// Return true if running in array mode - it means we can enter more then one entry
	const arrayMode = computed(() => {
		return sg.value.maxTags > 1 || sg.value.minTags > 1 || sg.value.array;
	});

	/* Minimum number of tags (cannot delete tag bellow this number) */
    const minTags = computed(() => {
        let min = sg.value.minTags || 0;
        if (!arrayMode.value && min > 1) min = 1;
        return min;
    });

    /* Maximum number of tags (cannot add tag above this number) */
    const maxTags = computed(() => {
        return arrayMode.value ? sg.value.maxTags || 1 : 1; // maxTags are ignored if data isn't array
    });

    const dataArray = computed<any[]>(() => {
        if (arrayMode.value) {
            if (!dataIsArray(ed.value.value)) ed.value.value = [];  // make it array
            return ed.value.value;
        } else {
            // Normalize to array
            return [ed.localData.value];
        }
    });

    const tagArray = computed<TagSegment[]>(() => {
        const tags:TagSegment[] = [];
        const dat = dataArray.value;
		//debugger;
		//console.log("Data array is " + JSON.stringify(dat));
        for (let i = 0; i < dat.length; i++) {
            const tag = dataToTag(dat[i], i);
            if (tag) tags.push(tag);
        }
        return tags;
    });

	/**
	 * Make meta array the same length as dataArray
	 */
	function initMetaArray():any[] {
		const m = []
		if (sg.value.maxTags > 1 || sg.value.minTags > 1 || sg.value.array) {
			let len;
			if (!dataIsArray(ed.value.value)) len = 0;
			else len = ed.value.value.length;
			for (let i = 0; i < len; i++) m.push({});
		} else m.push({});	// one object for non-array suggester
		return m;
	}

    const tagCount = computed<number>(() => {
        return tagArray.value.length;
    });

    const canAddMoreTags = computed<boolean>(() => {
        return tagCount.value < maxTags.value && !ed.ro.value;
    });


    function deleteData(dataIndex:number):void {
        if (arrayMode.value) {
            // Just remove data object from array
            ed.value.value.splice(dataIndex, 1);
			meta.splice(dataIndex, 1);
        } else {
            // We need to replace data with empty values
            propagateEmpty();
        }
    }

    /** Works only when array mode is false */
    function propagateEmpty():void {
        for (let rule of sg.value.propagate) {
            if (rule.nooverwrite) continue;
            let emptyVal = rule.empty;
            if (Array.isArray(emptyVal)) emptyVal = emptyVal[0];
            if (typeof emptyVal === "undefined") emptyVal = null;
            setter(ed.localData.value, rule.d, emptyVal, ed.formData);
        }
    }

    function dataObjectAt(index:number, insert:boolean = false) {
        if (arrayMode.value) {
            const ar = ed.value.value;
            if (insert && ar.length > index) {
				ar.splice(index, 0, {});
				meta.splice(index, 0, {});
			}
            while (ar.length <= index) {
				ar.push({});
				meta.push({});
			}
            return ar[index];
        } else {
            return ed.localData.value;
        }
    }

    function setDataObjectAt(index:number, value: any) {
        if (arrayMode.value) {
            const ar = ed.value.value;
            ar[index] = value;
        } else throw new Error("Propagation of suggestion using self-path ("+SELF_PATH+") is allowed only in array mode");
    }


	function metaObjectFor(index:number):any {
		if (typeof meta[index] === "undefined") meta[index] = {};
		return meta[index];
	}

	function getValue(path:string|number, dataObject:any, index:number):any {
		if (path === SELF_PATH) return dataObject;
		let applyTo = dataObject;
		if (typeof path === 'string' && path.startsWith("$.")) {
			applyTo = metaObjectFor(index);
			path = path.substring(2);
		}
		return getter(applyTo, path, ed.formData);
	}

	function setValue(path:string|number, dataObject:any, index:number, value:any):any {
		if (path === SELF_PATH) setDataObjectAt(index, value);
		let applyTo = dataObject;
		if (typeof path === 'string' && path.startsWith("$.")) {
			applyTo = metaObjectFor(index);
			path = path.substring(2);
		}
		setter(applyTo, path, value, ed.formData);
	}

    // { d: "<path.inside.data>", s: "<path.inside.sugggested.record>", c: "<conversion>", empty: 0 }
    function propagateSuggestion(suggestion:NormalizedSuggestionItem, index:number) {
        const dataObject = dataObjectAt(index, true);
        for (let rule of sg.value.propagate) {
            const dataPath = rule.d;
            const suggPath = rule.s;
            const convert = rule.c;
            let value = getter(suggestion, suggPath);
            if (typeof value === "undefined") {
                // this didn't come from the server
                if (typeof rule.ifmissing !== "undefined") value = rule.ifmissing;
                else continue;
            } else {
                if (convert) value = convertTo(value, convert);
            }
            if (rule.nooverwrite) {
                // check if target is empty
                const currentValue = getValue(dataPath, dataObject, index);
                if (!valueEmpty(currentValue, rule.empty)) continue;
            }
			setValue(dataPath, dataObject, index, value);
        }
    }

    function isDuplicate(suggestion:NormalizedSuggestionItem):boolean {
        if (arrayMode.value) {
			let index = 0;
            for (let dataObject of ed.value.value) {
                let match = true;
                for (let rule of sg.value.propagate) {
                    const dataPath = rule.d;
                    const suggPath = rule.s;
                    const convert = rule.c;
                    let value = getter(suggestion, suggPath);
                    if (typeof value === "undefined") {
                        // this didn't come from the server
                        if (typeof rule.ifmissing !== "undefined") value = rule.ifmissing;
                        else continue;
                    } else {
                        if (convert) value = convertTo(value, convert);
                    }
					const currentValue = getValue(dataPath, dataObject, index);
                    match = match && value === currentValue;
                }
                if (match) return true;
				index++;
            }
        }
        return false;
    }

    function getPropertyIdValue(dataPath: string, index: number):any {
        const dataObject = dataObjectAt(index);
		const value = getValue(dataPath, dataObject, index);
        if (valueEmpty(value, [0, ""])) return null;
        return value;
    }

    function convertTo(value:any, type:Cast) {
        switch (type) {
            case "string":
                if (typeof value !== "string") value = value.toString();
                break;
            case "integer":
                if (typeof value === "number") value = Math.trunc(value);
                else if (typeof value === "string") value = parseInt(value);
                else value = 0;
                break;
            case "number":
                if (typeof value === "string") value = parseFloat(value);
                else if (typeof value !== "number") value = 0;
                break;
            case "boolean":
                value = !!value;
                break;
        }
        return value;
    }

    function valueEmpty(value:any, consideredEmpty: any):boolean {
        if (value == null) return true;
        if (typeof value === "string" && value.length === 0) return true; // empty string
        if (typeof consideredEmpty === "undefined") return false;
        if (Array.isArray(consideredEmpty)) {
            // multiple empty values
            for (let em of consideredEmpty) {
                if (value === em) return true;
            }
            return false;
        } else return value === consideredEmpty;
    }

    function setTagProperty(tag:TagSegment, tagProperty:TagProperty, value:any) {
        switch (tagProperty) {
            case "name":
                tag.name = value;
                break;
            case "icon":
                tag.icon = value;
                break;
            case "href":
                tag.href = value;
                break;
            case "title":
                tag.title = value.toString();
                break;
            case "id":
                tag.key = value.toString();
                tag.id = value;
                break;
        }
    }

    function buildReverseSuggesterEndpoint(query:ReverseSuggesterQuery, dataObject:any, index:number):string {
        let url = query.url;
        if (sg.value.suggester) url = env.net.urlParam(url, "suggester", sg.value.suggester);
        if (query.params) {
            for (let param of query.params) {
				let paramValue = getValue(param.d, dataObject, index);
                if (!valueEmpty(paramValue, param.empty)) url = env.net.urlParam(url, param.name, paramValue);
            }
        }
        return url;
    }

	function dataToTag(dataObject: any, index = 0): TagSegment | null {
		// Propagate data (must be reactive to support async name loading with reverse suggest)
		const tag: TagSegment = reactive({
			key: "object" + index,
			name: "",
			type: "tag",
			index: index,
		});

		if (typeof dataObject === "string") {
			tag.name = dataObject;
		} else if (typeof dataObject === "number") {
			tag.name = dataObject.toString();
		}

		for (let rule of sg.value.tag) {
			const pathToData = rule.d;
			const tagProperty = rule.t;
			const emptyValues = rule.empty;
			const optional = !!rule.optional;
			// Obtain property from data
			let value = getValue(pathToData, dataObject, index);
			const isEmpty = valueEmpty(value, emptyValues);
			if (isEmpty && !rule.loadIfMissing) {
				if (!optional) return null; // empty data because required property is empty
			} else {
				setTagProperty(tag, tagProperty, isEmpty ? "" : value);
				if (isEmpty && rule.loadIfMissing) {
					// value must be loaded
					const endpoint = buildReverseSuggesterEndpoint(rule.loadIfMissing, dataObject, index);
					setTagProperty(tag, tagProperty, "loading...");
					//console.log("Fetching " + endpoint);
					env.net.fetch(endpoint, "GET")
						.then((response) => {
							if (response.ok) {
								response.text()
									.then((fetchedValue) => {
										//console.log("  loaded " + fetchedValue);
										setTagProperty(tag, tagProperty, fetchedValue);
										setValue(pathToData, dataObject, index, fetchedValue); // insert to data
									});
							}
						});
				}
			}
		}
		return tag;
	}

    function buildSuggesterUrl(dataIdx:number):string {
        let url = sg.value.url;
        if (sg.value.params) {
            const dobj = dataObjectAt(dataIdx);
            for (let p of sg.value.params) {
                const val = p.d ? getter(dobj, p.d, ed.formData) : dobj;
                if (typeof val !== "undefined" && !valueEmpty(val, p.empty)) {
                    url = env.net.urlParam(url, p.name, val.toString());
                }
            }
        }
        return url;
    }


    return {
        ...ed,
        // Computed
        minTags,maxTags,arrayMode,dataArray,tagArray,tagCount,canAddMoreTags,
        // Methods
        dataToTag, valueEmpty, convertTo, getPropertyIdValue,
        propagateSuggestion, dataObjectAt, propagateEmpty, deleteData,
        buildSuggesterUrl, isDuplicate, meta
    }
}
